// Libs
import classNames from 'classnames/bind';
import { uid } from 'uid';
import { useParams } from 'react-router-dom';
import { DndContext } from '@dnd-kit/core';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { ItemChangedReason } from 'dnd-kit-sortable-tree/dist/types';
import { useEffect, useState } from 'react';
import { SortableTree, TreeItems } from 'dnd-kit-sortable-tree';
import { Box, Button, Stack, Table, TableCell, TableHead, TableRow, Typography } from '@mui/material';
// Components, Layouts, Pages
import { ItemGroupQuote } from '~/components/module/quotes';
// Others
import GroupContext from '~/context/groupContext/GroupContext';
import { ITreeItem } from '~/utils/interface/common';
import { DiscountTypeEnum, TypeListInventory } from '~/utils/enum/common';
import { formatCurrencyUSD, handleCalculateDiscountedPrice } from '~/utils/helper/common';
import {
  IFormQuotes,
  IProductGroup,
  IQuoteAddOn,
  IQuoteGroup,
  IQuoteItem,
  IQuoteListItem,
} from '~/utils/interface/quotes';
import { DEFAULT_INDEX, EMPTY_STRING } from '~/utils/constants/common';
// Styles, images, icons
import styles from './GroupsQuoteForm.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const GroupsQuoteForm = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/quotes', 'common/base']);
  const { id: quoteId } = useParams();

  const {
    setValue,
    watch,
    formState: {},
  } = useFormContext<IFormQuotes>();

  const {
    laborCost,
    productSubtotal,
    discountRate,
    listQuoteItems,
    listQuoteItemsSelected,
    selectGroupQuote,
    selectGroupItem,
    selectIdProductQuote,
    selectIdProductGroup,
    selectIdVariantQuote,
    selectIdVariantGroup,
    quoteGroups,
    pricingTotal,
    templateQuoteId,
    quoteItems,
    quoteAddOns,
    deleteItemIds,
  } = watch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [items, setItems] = useState<TreeItems<ITreeItem<IProductGroup>>>([]);
  const [indexGroup, setIndexGroup] = useState<number>(DEFAULT_INDEX);

  const [isCheckDetail, setIsCheckDetail] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!quoteId || isCheckDetail) return;

    // Handle Set Data Group Detail
    const itemsData = handleConvertQuoteDataDetailToTreeItem(quoteGroups || [], quoteItems || [], quoteAddOns || []);

    if (itemsData?.length > 0) {
      setItems(itemsData);
      setIsCheckDetail(true);
    }
  }, [
    quoteId,
    JSON.stringify(listQuoteItems),
    JSON.stringify(quoteGroups),
    JSON.stringify(quoteItems),
    JSON.stringify(quoteAddOns),
  ]);

  useEffect(() => {
    if (!templateQuoteId) return;

    const itemsData = handleConvertQuoteDataDetailToTreeItem(quoteGroups || [], quoteItems || [], quoteAddOns || []);

    if (itemsData?.length > 0) {
      setItems(itemsData);
      setIsCheckDetail(true);
    }
  }, [templateQuoteId]);

  useEffect(() => {
    const dataQuoteGroup = handleGetListDataQuoteGroups(items);
    const dataQuoteItems = handleGetListDataQuoteItems(items);
    const dataQuoteAddOns = handleGetListDataQuoteItemsAddOn(items);
    const newProductSubtotal = handleCalculateTotal(items);

    setValue('quoteGroups', dataQuoteGroup);
    setValue('quoteItems', dataQuoteItems);
    setValue('quoteAddOns', dataQuoteAddOns);
    setValue('productSubtotal', newProductSubtotal);

    setValue(
      'laborCost',
      dataQuoteItems?.reduce<number>((sum, item) => sum + (Number(item?.laborCost) || 0), 0) +
        dataQuoteAddOns?.reduce<number>((sum, item) => sum + (Number(item?.laborCost) || 0), 0)
    );
  }, [JSON.stringify(items), pricingTotal]);

  useEffect(() => {
    if (!listQuoteItems) return;

    const selectedId = selectIdProductGroup || selectIdProductQuote;
    const selectedVariantId = selectIdVariantGroup || selectIdVariantQuote;

    // const mergedList = [
    //   ...(listQuoteItemsSelected || []),
    //   ...listQuoteItems.filter(
    //     (item) =>
    //       !listQuoteItemsSelected?.find(
    //         (selected) =>
    //           selected.id == item.id && (selected.variantId || EMPTY_STRING) === (item.variantId || EMPTY_STRING)
    //       )
    //   ),
    // ];

    const itemSelected = listQuoteItems?.find(
      (item) =>
        item?.id === selectedId && String(item?.variantId || EMPTY_STRING) === String(selectedVariantId || EMPTY_STRING)
    );

    const isExist = listQuoteItemsSelected?.some(
      (item) =>
        item?.id === selectedId && String(item?.variantId || EMPTY_STRING) === String(selectedVariantId || EMPTY_STRING)
    );

    if (!isExist && itemSelected) {
      setValue('listQuoteItemsSelected', [...(listQuoteItemsSelected || []), itemSelected]);
    }

    if (
      !selectedId ||
      !listQuoteItems?.some(
        (item) =>
          item?.id === selectedId &&
          String(item?.variantId || EMPTY_STRING) === String(selectedVariantId || EMPTY_STRING)
      )
    ) {
      return;
    }

    const newItems = handleAddQuoteItemsToGroup(
      items,
      listQuoteItems,
      selectGroupItem || selectGroupQuote,
      selectedId,
      selectedVariantId
    );

    setItems(newItems);
  }, [
    selectGroupQuote,
    selectGroupItem,
    selectIdProductGroup,
    selectIdProductQuote,
    selectIdVariantGroup,
    selectIdVariantQuote,
    JSON.stringify(listQuoteItems),
  ]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleCalcPackageTotal = (packageNode: ITreeItem<IProductGroup>): number => {
    if (!packageNode) return 0;

    const pricingMultiplier = Number(pricingTotal || 1);

    const packageOriginal = listQuoteItemsSelected?.find(
      (item) =>
        String(item?.id) === String(packageNode?.data?.inventoryId) &&
        String(item?.variantId || EMPTY_STRING) === String(packageNode?.data?.variantId || EMPTY_STRING)
    );

    const baseRaw = Number(packageNode?.data?.price || 0) * Number(packageNode?.data?.quantity || 1);

    const baseAfterDiscount = handleCalculateDiscountedPrice(
      baseRaw,
      Number(packageNode?.data?.discountRate || 0),
      packageNode?.data?.discountType || EMPTY_STRING
    );

    if (!packageOriginal || !packageOriginal.packageItems?.length) {
      return baseAfterDiscount * pricingMultiplier;
    }

    let extraTotal = 0;

    packageNode.children?.forEach((child) => {
      const childData = child.data;
      if (!childData) return;

      const qty = Number(childData.quantity || 0);
      const price = Number(childData.price || 0);

      if (!childData.isRequired && childData?.isShow) {
        const raw = qty * price;

        const afterDiscount = handleCalculateDiscountedPrice(
          raw,
          Number(childData?.discountRate || 0),
          childData?.discountType || EMPTY_STRING
        );

        extraTotal += afterDiscount;
      }
    });

    return (baseAfterDiscount + extraTotal) * pricingMultiplier;
  };

  const handleCalculateTotal = (items: TreeItems<ITreeItem<IProductGroup>>): number => {
    const calcNodeTotal = (node: ITreeItem<IProductGroup>): number => {
      if (node?.level === 1) {
        const groupTotal = (node.children || []).reduce((sum, child) => sum + calcNodeTotal(child), 0);
        return groupTotal;
      }

      if (node?.data?.type === TypeListInventory.PACKAGE) {
        return handleCalcPackageTotal(node);
      }

      const pricingMultiplier = Number(pricingTotal || 1);

      if (node?.data?.type === TypeListInventory.PRODUCT) {
        const basePrice = Number(node?.data?.price || 0) * Number(node?.data?.quantity || 0);

        const discountedBase = handleCalculateDiscountedPrice(
          basePrice,
          Number(node?.data?.discountRate || 0),
          node?.data?.discountType || EMPTY_STRING
        );

        const childrenTotal = (node.children || []).reduce((sum, child) => sum + calcNodeTotal(child), 0);

        return (discountedBase + childrenTotal) * pricingMultiplier;
      }

      return Number(node?.data?.price || 0) * Number(node?.data?.quantity || 0) * pricingMultiplier;
    };

    const newSubTotal = items.reduce((sum, node) => sum + calcNodeTotal(node), 0);

    return newSubTotal;
  };

  const updateAvailableInventoryAddOn = (
    listQuoteItems: IQuoteListItem[],
    quoteAddOns: IQuoteAddOn[]
  ): IQuoteAddOn[] => {
    const variantMap = new Map<string, number>();
    const inventoryMap = new Map<string, number>();

    listQuoteItems.forEach((item) => {
      item.addOns?.forEach((ao) => {
        if (!ao.inventoryId) return;
        const inv = ao.inventoryId.toString();

        if (ao.isHasVariant && Array.isArray(ao.availableVariant)) {
          ao.availableVariant.forEach((variant) => {
            const key = `${inv}::${variant.id}`;
            variantMap.set(key, Number(variant.quantity ?? 0));
          });
        } else {
          if (!inventoryMap.has(inv)) inventoryMap.set(inv, Number(ao.availableInventory ?? 0));
        }
      });

      item.packageItems?.forEach((pi) => {
        if (!pi.inventoryId) return;
        const inv = pi.inventoryId.toString();
        const avail = Number(pi.availableInventory ?? 0);
        const vid = pi.variantId?.toString() ?? '';

        if (vid) {
          variantMap.set(`${inv}::${vid}`, avail);
        } else {
          if (!inventoryMap.has(inv)) inventoryMap.set(inv, avail);
        }
      });
    });

    const newAddOns = quoteAddOns.map((qa) => {
      const inv = qa.inventoryId ? qa.inventoryId.toString() : '';
      const vid = qa.variantId ? qa.variantId.toString() : '';

      const variantKey = `${inv}::${vid}`;
      if (vid && variantMap.has(variantKey)) {
        return { ...qa, availableInventory: String(variantMap.get(variantKey) ?? 0) };
      }

      if (inv && inventoryMap.has(inv)) {
        return { ...qa, availableInventory: String(inventoryMap.get(inv) ?? 0) };
      }

      return { ...qa, availableInventory: '0' };
    });

    return newAddOns;
  };

  const updateAvailableInventoryItem = (listQuoteItems: IQuoteListItem[], quoteItems: IQuoteItem[]): IQuoteItem[] => {
    const inventoryMap = new Map<string, string>();

    listQuoteItems.forEach((res) => {
      if (!res.id) return;

      const key = `${res.id}_${res.variantId ?? 'null'}`;
      inventoryMap.set(key, res.quantityInventory ?? EMPTY_STRING);
    });

    const newQuoteItems = quoteItems.map((qi) => {
      if (!qi.inventoryId) {
        return {
          ...qi,
          availableInventory: EMPTY_STRING,
        };
      }

      const key = `${qi.inventoryId}_${qi.variantId ?? 'null'}`;

      return {
        ...qi,
        availableInventory: inventoryMap.get(key) ?? EMPTY_STRING,
      };
    });

    return newQuoteItems;
  };

  const handleConvertQuoteDataDetailToTreeItem = (
    quoteGroups: IQuoteGroup[],
    quoteItems: IQuoteItem[],
    quoteAddOns: IQuoteAddOn[]
  ): TreeItems<ITreeItem<IProductGroup>> => {
    const tree: TreeItems<ITreeItem<IProductGroup>> = [];

    const buildAddOns = (
      parentId?: string,
      parentProductId?: string,
      parentType?: string
    ): TreeItems<ITreeItem<IProductGroup>> => {
      const newAddOns = updateAvailableInventoryAddOn(listQuoteItems || [], quoteAddOns);

      const addOns = newAddOns?.filter((addOn) => addOn?.parentId === parentId);

      if (parentType !== TypeListInventory.PRODUCT) {
        return addOns.map((addOn) => {
          const totalItem = Number(addOn?.price || 0) * Number(addOn?.quantity || 0);
          const totalDiscount = handleCalculateDiscountedPrice(
            Number(totalItem || 0),
            Number(addOn?.discountRate || 0),
            addOn?.discountType || EMPTY_STRING
          );

          return {
            id: uid(),
            level: 3,
            data: {
              ...addOn,
              id: addOn?.id ?? EMPTY_STRING,
              productId: parentProductId ?? EMPTY_STRING,
              addOnId: addOn?.productId ?? EMPTY_STRING,
              idDetail: addOn?.id ?? EMPTY_STRING,
              isAddOn: true,
              total: totalDiscount,
              isRequired: addOn?.isRequired ?? false,
              isShowPrice: addOn?.isShowPrice ?? false,
              isOverbooking: addOn?.isOverbooking ?? false,
            },
            canHaveChildren: false,
          };
        });
      }

      const inventoryIdMap = new Map<string, boolean>();

      return addOns.map((addOn) => {
        const inventoryId = addOn.inventoryId ?? EMPTY_STRING;
        const hasVariant = Boolean(addOn?.variantId && inventoryId);
        const isFirst = hasVariant && !inventoryIdMap.has(inventoryId);

        if (hasVariant) {
          inventoryIdMap.set(inventoryId, true);
        }

        const totalItem = Number(addOn?.price || 0) * Number(addOn?.quantity || 0);
        const totalDiscount = handleCalculateDiscountedPrice(
          Number(totalItem || 0),
          Number(addOn?.discountRate || 0),
          addOn?.discountType || EMPTY_STRING
        );

        return {
          id: uid(),
          level: 3,
          data: {
            ...addOn,
            id: addOn?.id ?? EMPTY_STRING,
            productId: parentProductId ?? EMPTY_STRING,
            addOnId: addOn?.productId ?? EMPTY_STRING,
            isHasVariant: isFirst,
            idRemember: inventoryId,
            idDetail: addOn?.id ?? EMPTY_STRING,
            total: totalDiscount,
            isAddOn: true,
            isRequired: addOn?.isRequired ?? false,
            isShowPrice: addOn?.isShowPrice ?? false,
            isOverbooking: addOn?.isOverbooking ?? false,
          },
          canHaveChildren: false,
        };
      });
    };

    const buildItems = (parentId: string | null): TreeItems<ITreeItem<IProductGroup>> => {
      const newQuoteItems = updateAvailableInventoryItem(listQuoteItems || [], quoteItems);

      const items = newQuoteItems.filter((item) => item?.parentId === parentId);

      return items.map((item) => {
        const addOns = buildAddOns(item?.id, item?.productId, item?.type);

        const totalItem = Number(item?.price || 0) * Number(item?.quantity || 0);
        const totalDiscount = handleCalculateDiscountedPrice(
          Number(totalItem || 0),
          Number(item?.discountRate || 0),
          item?.discountType || EMPTY_STRING
        );

        return {
          id: uid(),
          level: 2,
          data: {
            ...item,
            id: item?.id ?? EMPTY_STRING,
            inventoryId: item?.inventoryId ?? EMPTY_STRING,
            name: item?.name,
            note: item?.note ?? EMPTY_STRING,
            price: item?.price ?? EMPTY_STRING,
            discountType: item?.discountType ?? EMPTY_STRING,
            discountRate: item?.discountRate ?? EMPTY_STRING,
            total: totalDiscount,
            category: {
              id: item?.categoryId ?? EMPTY_STRING,
              name: item?.categoryName ?? EMPTY_STRING,
            },
            idDetail: item?.id ?? EMPTY_STRING,
            isOverbooking: item?.isOverbooking ?? false,
          },
          canHaveChildren: true,
          children: addOns,
        };
      });
    };

    quoteGroups.forEach((group) => {
      const totalItem = Number(group?.price || 0);
      const totalDiscount = handleCalculateDiscountedPrice(
        Number(totalItem || 0),
        Number(group?.discountRate || 0),
        group?.discountType || EMPTY_STRING
      );

      tree.push({
        id: uid(),
        level: 1,
        data: {
          ...group,
          id: group?.id,
          name: group?.name ?? EMPTY_STRING,
          note: group?.note ?? EMPTY_STRING,
          discountType: group?.discountType ?? EMPTY_STRING,
          discountRate: group?.discountRate ?? EMPTY_STRING,
          price: group?.price ?? EMPTY_STRING,
          total: totalDiscount,
          idDetail: group?.id ?? EMPTY_STRING,
        },
        canHaveChildren: true,
        children: buildItems(group.id ?? EMPTY_STRING),
      });
    });

    tree.push(...buildItems(null));

    return tree;
  };

  const handleFindIdDetailItems = (
    nodes: TreeItems<ITreeItem<IProductGroup>>,
    matcher: (node: ITreeItem<IProductGroup>, parent?: ITreeItem<IProductGroup>) => boolean,
    parent?: ITreeItem<IProductGroup>
  ): string | undefined => {
    for (const node of nodes) {
      if (matcher(node, parent)) {
        return node.data?.idDetail;
      }
      if (node.children?.length) {
        const found = handleFindIdDetailItems(node.children, matcher, node);
        if (found) return found;
      }
    }
    return undefined;
  };

  const handleAddQuoteItemsToGroup = (
    prevItems: TreeItems<ITreeItem<IProductGroup>>,
    listQuoteItems: IQuoteListItem[],
    selectedGroupId?: string,
    selectedId?: string,
    selectedIdVariant?: string
  ): TreeItems<ITreeItem<IProductGroup>> => {
    const newItems = [...prevItems];

    let indexProduct = 99999;

    const dataFilterLevelOne = newItems.filter((item, index) => {
      const isSameProduct =
        item?.data?.inventoryId === selectedId &&
        (item?.data?.variantId || EMPTY_STRING) === (selectedIdVariant || EMPTY_STRING);

      if (isSameProduct) indexProduct = index;

      return !isSameProduct;
    });

    const newDataFilter = dataFilterLevelOne?.map((item) => {
      if (item?.level === 1 && item?.children?.length) {
        return {
          ...item,
          children: item.children.filter((child, index) => {
            const isSameChild =
              child?.data?.inventoryId === selectedId &&
              (child?.data?.variantId || EMPTY_STRING) === (selectedIdVariant || EMPTY_STRING);

            if (isSameChild) indexProduct = index;

            return !isSameChild;
          }),
        };
      }
      return item;
    });

    const productItems: TreeItems<ITreeItem<IProductGroup>> = [];

    listQuoteItems
      .filter((item) => Number(item?.quantity) > 0 && item?.id === selectedId)
      .forEach((item) => {
        const childrenAddOns: TreeItems<ITreeItem<IProductGroup>> = [];

        if (item?.addOns?.length && item?.type !== TypeListInventory.PACKAGE) {
          item?.addOns?.forEach((addOn) => {
            const addOnPrice = Number(addOn?.rentalPrice);
            const addOnQuantity = Number(addOn?.quantity) * Number(item?.quantity);

            const oldIdDetail = handleFindIdDetailItems(
              prevItems,
              (node, parent) =>
                (parent?.data?.inventoryId || EMPTY_STRING) === (item?.id || EMPTY_STRING) &&
                (parent?.data?.variantId || EMPTY_STRING) === (item?.variantId || EMPTY_STRING) &&
                node?.data?.addOnId === addOn?.addOnId
            );

            childrenAddOns.push({
              id: uid(),
              level: 3,
              canHaveChildren: false,
              data: {
                id: oldIdDetail || addOn?.id,
                idDetail: oldIdDetail || addOn?.id,
                name: addOn?.name,
                inventoryId: addOn?.inventoryId,
                availableInventory: addOn?.availableInventory,
                note: item?.note || EMPTY_STRING,
                price: addOnPrice,
                quantity: addOnQuantity,
                total: addOnPrice * addOnQuantity,
                discountType: DiscountTypeEnum.PERCENTAGE,
                discountRate: EMPTY_STRING,
                laborCost: EMPTY_STRING,
                timeLoading: addOn?.timeLoading || undefined,
                timeUnloading: addOn?.timeUnloading || undefined,
                timeDelivery: addOn?.timeDelivery || undefined,
                timePickup: addOn?.timePickup || undefined,
                addOnId: addOn?.addOnId || EMPTY_STRING,
                productId: item?.productId || EMPTY_STRING,
                duration: 0,
                isAddOn: true,
                isHasVariant: addOn?.isHasVariant,
                isRequired: addOn?.isRequired ?? false,
                isShow: true,
              },
            });
          });
        }

        if (item?.packageItems?.length && item?.type === TypeListInventory.PACKAGE) {
          item?.packageItems.forEach((packageItem) => {
            const packagePrice = Number(packageItem?.rentalPrice);
            const packageQuantity = Number(item?.quantity) * Number(packageItem?.quantity);

            const oldIdDetail = handleFindIdDetailItems(
              prevItems,
              (node, parent) =>
                (parent?.data?.inventoryId || EMPTY_STRING) === (item?.id || EMPTY_STRING) &&
                (parent?.data?.variantId || EMPTY_STRING) === (item?.variantId || EMPTY_STRING) &&
                node?.data?.inventoryId === packageItem?.inventoryId &&
                (node?.data?.variantId || EMPTY_STRING) === (packageItem?.variantId || EMPTY_STRING)
            );

            childrenAddOns.push({
              id: uid(),
              level: 3,
              canHaveChildren: false,
              data: {
                id: oldIdDetail || packageItem?.id,
                idDetail: oldIdDetail,
                name: packageItem?.name,
                inventoryId: packageItem?.inventoryId || EMPTY_STRING,
                note: item?.note || EMPTY_STRING,
                price: packageItem?.isRequired ? 0 : packagePrice,
                quantity: packageQuantity,
                total: packagePrice * packageQuantity,
                discountType: DiscountTypeEnum.PERCENTAGE,
                discountRate: EMPTY_STRING,
                laborCost: EMPTY_STRING,
                duration: 0,
                isAddOn: true,
                timeDelivery: packageItem?.timeDelivery || undefined,
                timePickup: packageItem?.timePickup || undefined,
                timeLoading: packageItem?.timeLoading || undefined,
                timeUnloading: packageItem?.timeUnloading || undefined,
                variantId: packageItem?.variantId,
                variantName: packageItem?.variantName,
                type: packageItem?.type || EMPTY_STRING,
                availableInventory: packageItem?.availableInventory || '0',
                isRequired: packageItem?.isRequired ?? false,
                isShowPrice: packageItem?.isShowPrice ?? false,
                isShow: packageItem?.isRequired ? true : false,
              },
            });
          });
        }

        const oldIdDetail = handleFindIdDetailItems(prevItems, (node, parent) => {
          return (
            (parent?.id || EMPTY_STRING) === (selectedGroupId || EMPTY_STRING) &&
            node?.data?.inventoryId === item?.id &&
            (node?.data?.variantId || EMPTY_STRING) === (item?.variantId || EMPTY_STRING)
          );
        });

        productItems.push({
          id: uid(),
          level: 2,
          canHaveChildren: true,
          data: {
            id: oldIdDetail || item?.id,
            idDetail: oldIdDetail,
            inventoryId: item?.id,
            name: item?.name || EMPTY_STRING,
            note: item?.note || EMPTY_STRING,
            price: Number(item?.rentalPrice),
            quantity: Number(item?.quantity),
            total: Number(item?.rentalPrice) * Number(item?.quantity),
            discountType: DiscountTypeEnum.PERCENTAGE,
            discountRate: EMPTY_STRING,
            timeDelivery: item?.timeDelivery || undefined,
            timePickup: item?.timePickup || undefined,
            timeLoading: item?.timeLoading || undefined,
            timeUnloading: item?.timeUnloading || undefined,
            laborCost: EMPTY_STRING,
            duration: 0,
            isAddOn: false,
            variantId: item?.variantId,
            variantName: item?.variantName || EMPTY_STRING,
            availableInventory: item?.quantityInventory || '0',
            type: item?.type || EMPTY_STRING,
            category: item?.category,
            isShow: true,
          },
          children: childrenAddOns?.length > 0 ? childrenAddOns : undefined,
        });
      });

    const updatedGroupIndex = newDataFilter?.findIndex((item) => item?.id === selectedGroupId && item?.level === 1);

    if (updatedGroupIndex !== -1) {
      const group = newDataFilter[updatedGroupIndex];
      group.children = [
        ...(group?.children?.slice(0, indexProduct) || []),
        ...productItems,
        ...(group?.children?.slice(indexProduct) || []),
      ];
    } else {
      newDataFilter.splice(indexProduct, 0, ...productItems);
    }

    return newDataFilter;
  };

  const handleGetListDataQuoteGroups = (items: TreeItems<ITreeItem<IProductGroup>>): IQuoteGroup[] => {
    const dataGroupOne: TreeItems<ITreeItem<IProductGroup>> = items?.filter((item) => item?.level === 1);

    const dataQuoteGroups: IQuoteGroup[] = dataGroupOne.map((item) => {
      const editPrice = Number(item?.data?.price || 0);
      const calculatedPrice = handleGetDataLevelPriceTotal(item)?.price || 0;
      const price = editPrice > 0 ? editPrice : calculatedPrice;

      return {
        id: item?.data?.id || EMPTY_STRING,
        note: item?.data?.note || EMPTY_STRING,
        name: item?.data?.name || EMPTY_STRING,
        discountType: item?.data?.discountType || EMPTY_STRING,
        discountRate: item?.data?.discountRate || EMPTY_STRING,
        price: price,
        total: handleGetDataLevelPriceTotal(item)?.total || 0,
        idDetail: item?.data?.idDetail || EMPTY_STRING,
      };
    });

    return dataQuoteGroups;
  };

  const handleGetDataLevelPriceTotal = (item: ITreeItem<IProductGroup>): { price: number; total: number } => {
    if (!item || !item?.children) {
      return { price: 0, total: 0 };
    }

    const calculateRecursive = (nodes: TreeItems<ITreeItem<IProductGroup>>): { price: number; total: number } => {
      let price = 0;
      let total = 0;

      nodes.forEach((node) => {
        price +=
          handleCalculateDiscountedPrice(
            Number(node?.data?.price),
            Number(node?.data?.discountRate),
            node?.data?.discountType || EMPTY_STRING
          ) || 0;

        total += node?.data?.total || 0;

        if (node?.children && node?.children?.length > 0) {
          const childTotals = calculateRecursive(node?.children);
          price += childTotals.price;
          total += childTotals.total;
        }
      });

      return { price, total };
    };

    return calculateRecursive(item?.children);
  };

  const handleGetListDataQuoteItems = (treeItems: TreeItems<ITreeItem<IProductGroup>>): IQuoteItem[] => {
    const data: IQuoteItem[] = [];

    const dataOutsideGroup: TreeItems<ITreeItem<IProductGroup>> = treeItems.filter(
      (item) => item?.level === 2 && !item?.data?.isAddOn
    );

    let indexItemGroup = 0;

    dataOutsideGroup.forEach((item) => {
      const totalItem = Number(item?.data?.price || 0) * Number(item.data?.quantity || 0) * Number(pricingTotal || 1);
      const totalDiscount = handleCalculateDiscountedPrice(
        Number(totalItem || 0),
        Number(item?.data?.discountRate || 0),
        item?.data?.discountType || EMPTY_STRING
      );

      data.push({
        id: item?.data?.id,
        inventoryId: item?.data?.inventoryId || EMPTY_STRING,
        note: item?.data?.note || EMPTY_STRING,
        name: item?.data?.name || EMPTY_STRING,
        timeUnloading: item?.data?.timeUnloading || undefined,
        timeLoading: item?.data?.timeLoading || undefined,
        timeDelivery: item?.data?.timeDelivery || undefined,
        timePickup: item?.data?.timePickup || undefined,
        quantity: item?.data?.quantity || 0,
        laborCost: item?.data?.laborCost || EMPTY_STRING,
        price: item?.data?.price || 0,
        discountType: item?.data?.discountType || EMPTY_STRING,
        discountRate: item?.data?.discountRate || EMPTY_STRING,
        duration: item?.data?.duration || 0,
        additionalHours: item?.data?.additionalHours || 0,
        total: totalDiscount,
        type: item?.data?.type,
        variantId: item?.data?.variantId,
        variantName: item?.data?.variantName,
        category: item?.data?.category,
        idDetail: item?.data?.idDetail,
        isOverbooking: item?.data?.isOverbooking || false,
        availableInventory: item?.data?.availableInventory || '0',
      });
    });

    treeItems.forEach((groupItem) => {
      if (groupItem.children && groupItem.level === 1) {
        groupItem.children.forEach((item) => {
          if (item?.level === 2 && !item?.data?.isAddOn) {
            const totalItem =
              Number(item?.data?.price || 0) * Number(item.data?.quantity || 0) * Number(pricingTotal || 1);

            const totalDiscount = handleCalculateDiscountedPrice(
              Number(totalItem || 0),
              Number(item?.data?.discountRate || 0),
              item?.data?.discountType || EMPTY_STRING
            );

            data.push({
              id: item?.data?.id,
              inventoryId: item?.data?.inventoryId || EMPTY_STRING,
              note: item?.data?.note || EMPTY_STRING,
              name: item?.data?.name || EMPTY_STRING,
              quantity: item?.data?.quantity || 0,
              laborCost: item?.data?.laborCost || EMPTY_STRING,
              timeUnloading: item?.data?.timeUnloading || undefined,
              timeLoading: item?.data?.timeLoading || undefined,
              timeDelivery: item?.data?.timeDelivery || undefined,
              timePickup: item?.data?.timePickup || undefined,
              price: item?.data?.price || 0,
              discountType: item?.data?.discountType || EMPTY_STRING,
              discountRate: item?.data?.discountRate || EMPTY_STRING,
              duration: item?.data?.duration || 0,
              additionalHours: item?.data?.additionalHours || 0,
              total: totalDiscount,
              type: item?.data?.type,
              indexGroup: indexItemGroup,
              variantId: item?.data?.variantId,
              variantName: item?.data?.variantName,
              category: item?.data?.category,
              idDetail: item?.data?.idDetail,
              isOverbooking: item?.data?.isOverbooking || false,
              availableInventory: item?.data?.availableInventory || '0',
            });
          }
        });

        indexItemGroup += 1;
      }
    });

    return data;
  };

  const handleGetListDataQuoteItemsAddOn = (treeItems: TreeItems<ITreeItem<IProductGroup>>): IQuoteAddOn[] => {
    const data: IQuoteAddOn[] = [];

    let quoteItemIndex = 0;

    treeItems.forEach((node) => {
      if (node?.level === 2 && !node?.data?.isAddOn) {
        if (node?.children?.length) {
          node.children.forEach((child) => {
            if (child?.level === 3 && child?.data?.isAddOn) {
              const totalItem =
                Number(child?.data?.price || 0) * Number(child?.data?.quantity || 0) * Number(pricingTotal || 1);

              const totalDiscount = handleCalculateDiscountedPrice(
                Number(totalItem || 0),
                Number(child?.data?.discountRate || 0),
                child?.data?.discountType || EMPTY_STRING
              );

              data.push({
                id: child?.data?.id,
                inventoryId: child?.data?.inventoryId || EMPTY_STRING,
                note: child?.data?.note || EMPTY_STRING,
                name: child?.data?.name || EMPTY_STRING,
                quantity: child?.data?.quantity || 0,
                laborCost: child?.data?.laborCost || EMPTY_STRING,
                price: child?.data?.price || 0,
                discountType: child?.data?.discountType || EMPTY_STRING,
                discountRate: child?.data?.discountRate || EMPTY_STRING,
                duration: child?.data?.duration || 0,
                additionalHours: child?.data?.additionalHours || 0,
                total: totalDiscount,
                timeUnloading: child?.data?.timeUnloading,
                timeLoading: child?.data?.timeLoading,
                timeDelivery: child?.data?.timeDelivery,
                timePickup: child?.data?.timePickup,
                indexItem: quoteItemIndex,
                variantId: child?.data?.variantId,
                variantName: child?.data?.variantName,
                isRequired: child?.data?.isRequired,
                isShowPrice: child?.data?.isShowPrice ?? false,
                idDetail: child?.data?.idDetail,
                isOverbooking: child?.data?.isOverbooking || false,
                availableInventory: child?.data?.availableInventory || '0',
                idRemember: child?.data?.idRemember || EMPTY_STRING,
                isHasVariant: child?.data?.isHasVariant,
                isShow: child?.data?.isShow,
              });
            }
          });
        }

        quoteItemIndex++;
      }
    });

    treeItems.forEach((groupNode) => {
      if (groupNode?.level === 1 && groupNode?.children?.length) {
        groupNode.children.forEach((itemNode) => {
          if (itemNode?.level === 2 && !itemNode?.data?.isAddOn) {
            if (itemNode?.children?.length) {
              itemNode.children.forEach((child) => {
                if (child?.level === 3 && child?.data?.isAddOn) {
                  const totalItem =
                    Number(child?.data?.price || 0) * Number(child?.data?.quantity || 0) * Number(pricingTotal || 1);

                  const totalDiscount = handleCalculateDiscountedPrice(
                    Number(totalItem || 0),
                    Number(child?.data?.discountRate || 0),
                    child?.data?.discountType || EMPTY_STRING
                  );

                  data.push({
                    id: child?.data?.id,
                    inventoryId: child?.data?.inventoryId || EMPTY_STRING,
                    note: child?.data?.note || EMPTY_STRING,
                    name: child?.data?.name || EMPTY_STRING,
                    quantity: child?.data?.quantity || 0,
                    laborCost: child?.data?.laborCost || EMPTY_STRING,
                    price: child?.data?.price || 0,
                    discountType: child?.data?.discountType || EMPTY_STRING,
                    discountRate: child?.data?.discountRate || EMPTY_STRING,
                    duration: child?.data?.duration || 0,
                    additionalHours: child?.data?.additionalHours || 0,
                    total: totalDiscount,
                    timeUnloading: child?.data?.timeUnloading,
                    timeLoading: child?.data?.timeLoading,
                    timeDelivery: child?.data?.timeDelivery,
                    timePickup: child?.data?.timePickup,
                    indexItem: quoteItemIndex,
                    variantId: child?.data?.variantId,
                    variantName: child?.data?.variantName,
                    isRequired: child?.data?.isRequired,
                    isShowPrice: child?.data?.isShowPrice ?? false,
                    idDetail: child?.data?.idDetail,
                    isOverbooking: child?.data?.isOverbooking || false,
                    availableInventory: child?.data?.availableInventory || '0',
                    idRemember: child?.data?.idRemember || EMPTY_STRING,
                    isHasVariant: child?.data?.isHasVariant,
                    isShow: child?.data?.isShow,
                  });
                }
              });
            }

            quoteItemIndex++;
          }
        });
      }
    });

    return data;
  };

  const handleAddGroup = () => {
    setItems([
      ...items,
      {
        id: uid(),
        level: 1,
        canHaveChildren: true,
        data: {
          id: uid(),
          name: `Group ${indexGroup}`,
          note: EMPTY_STRING,
          discountType: DiscountTypeEnum.PERCENTAGE,
          discountRate: EMPTY_STRING,
          price: 0,
          total: 0,
        },
      },
    ]);

    setIndexGroup((prev) => prev + 1);
  };

  const handleRemoveGroup = () => {
    const newItems: TreeItems<ITreeItem<IProductGroup>> = [];
    const deleteIds: string[] = [];

    items.forEach((item) => {
      if (item?.level === 1) {
        if (item?.data?.idDetail && !isNaN(Number(item?.data?.idDetail))) {
          deleteIds?.push(String(item?.data?.idDetail));
        }

        if (item?.children) {
          item?.children.forEach((child: ITreeItem<IProductGroup>) => {
            newItems.push({
              ...child,
              id: uid(),
              level: 2,
              canHaveChildren: true,
            });
          });
        }
      }

      if (item?.level !== 1) {
        newItems.push(item);
      }
    });

    if (deleteIds.length > 0) {
      setValue('deleteItemIds', [...(deleteItemIds || []), ...deleteIds]);
    }

    setItems(newItems);
  };

  const handleItemsChanged = (
    items: TreeItems<ITreeItem<IProductGroup>>,
    reason: ItemChangedReason<ITreeItem<IProductGroup>>
  ) => {
    if (reason.type === 'dropped') {
      const draggedItem = reason.draggedItem;
      const droppedToParent = reason.droppedToParent;

      if (draggedItem?.level === 1 && droppedToParent) return;

      if (draggedItem?.level === 2 && droppedToParent?.level === 2) return;

      if (draggedItem?.level === 3) return;

      setItems(items);
    } else {
      setItems(items);
    }
  };

  const handleGroupByCategory = () => {
    const categoryGroup =
      quoteItems
        ?.map((item) => item?.category)
        .filter((item) => item?.id && item?.name)
        .filter(
          (item, index, self) =>
            index === self?.findIndex((category) => category?.id === item?.id && category?.name === item?.name)
        ) || [];

    const dataItemsGroup: TreeItems<ITreeItem<IProductGroup>> = [];

    // Remove Group Level 1
    items.forEach((item) => {
      if (item?.level === 1 && item?.children) {
        item?.children.forEach((child) => {
          dataItemsGroup?.push({ ...child });
        });
      } else if (item?.level !== 1) {
        dataItemsGroup?.push(item);
      }
    });

    const newItems: TreeItems<ITreeItem<IProductGroup>> = [];

    // Create Group To Category
    categoryGroup.forEach((category) => {
      const groupChildren = dataItemsGroup?.filter(
        (child) => child?.level === 2 && child?.data?.category?.id === category?.id
      );

      if (groupChildren.length > 0) {
        newItems.push({
          id: uid(),
          level: 1,
          canHaveChildren: true,
          data: {
            id: uid(),
            name: category?.name,
            note: EMPTY_STRING,
            price: 0,
            discountType: DiscountTypeEnum.PERCENTAGE,
            discountRate: EMPTY_STRING,
            total: 0,
          },
          children: groupChildren.map((child) => ({
            ...child,
            level: 2,
          })),
        });
      }
    });

    // Add Date Level 2 No Category
    const dataNoCategory = dataItemsGroup.filter((child) => !child?.data?.category?.id);

    newItems.push(...dataNoCategory);

    setItems(newItems);
  };

  // const handleLog = () => {
  //   console.log(JSON.stringify(items, null, 2));
  // };
  //#endregion Handle Function

  return (
    <Stack className={cx('container')}>
      <Stack direction={'row'} alignItems={'center'}>
        <Typography className={cx('title')}>{t('form_groups_title_products')}</Typography>
      </Stack>

      <Stack className={cx('viewAction')}>
        {/* <Stack width={570} gap={4} direction={'row'} alignItems={'center'}> */}
        <Stack width={416} gap={4} direction={'row'} alignItems={'center'}>
          {/* <Button
            variant='outlined'
            className={cx('buttonGroup')}
            startIcon={<img src={icons.commonIconPlusPrimary} alt={t('common_alt_img')} />}
            onClick={handleLog}
          >
            {t('form_groups_title_new_group')}
          </Button> */}

          <Button
            variant='outlined'
            className={cx('buttonGroup')}
            startIcon={<img src={icons.commonIconPlusPrimary} alt={t('common_alt_img')} />}
            onClick={handleAddGroup}
          >
            {t('form_groups_title_new_group')}
          </Button>

          {/* <Button variant='contained' className={cx('buttonGroup')} onClick={handleGroupByCategory}>
            {t('form_groups_title_group_by_category')}
          </Button> */}

          <Button
            variant='contained'
            className={cx('buttonGroup')}
            startIcon={<img src={icons.commonIconClose} alt={t('common_alt_img')} />}
            onClick={handleRemoveGroup}
          >
            {t('form_groups_title_remove_groups')}
          </Button>
        </Stack>
      </Stack>

      <GroupContext.Provider value={{ items, setItems }}>
        <DndContext>
          <Box className={cx('viewTable')}>
            <Table stickyHeader>
              <TableHead className={cx('tableHead')}>
                <TableRow>
                  <TableCell width='20%' className={cx('titleHead')}>
                    {t('form_groups_title_group_name')}
                  </TableCell>
                  <TableCell width='15%' className={cx('titleHead')}>
                    {t('form_groups_title_discount')}
                  </TableCell>
                  <TableCell width='12%' className={cx('titleHead')}>
                    {t('form_groups_title_price')}
                  </TableCell>
                  <TableCell width='12%' className={cx('titleHead')}>
                    {t('form_groups_title_labor_cost')}
                  </TableCell>
                  <TableCell width='14%' className={cx('titleHead')} align='center'>
                    {t('form_groups_title_quantity')}
                  </TableCell>
                  <TableCell width='12%' className={cx('titleHead')}>
                    {t('form_groups_title_total')}
                  </TableCell>
                  <TableCell width='5%' className={cx('titleHead')}>
                    <Box width='24px' height='24px' />
                  </TableCell>
                </TableRow>
              </TableHead>
            </Table>

            <Stack>
              <SortableTree
                items={items || []}
                onItemsChanged={handleItemsChanged}
                TreeItemComponent={ItemGroupQuote}
                indentationWidth={25}
              />
            </Stack>
          </Box>
        </DndContext>
      </GroupContext.Provider>

      <Stack alignItems={'flex-end'} gap={'14px'} className={cx('viewFooter')}>
        <Stack direction={'row'} gap={2} width={'306px'} alignItems={'center'} justifyContent={'space-between'}>
          <Box className={cx('titleLabel')}>{t('form_groups_title_product_subtotal')}</Box>
          <Box className={cx('titleValue')}>
            {formatCurrencyUSD({ value: Number(productSubtotal || 0) - Number(discountRate || 0), showDecimal: true })}
          </Box>
        </Stack>

        <Stack direction={'row'} gap={2} width={'306px'} alignItems={'center'} justifyContent={'space-between'}>
          <Box className={cx('titleLabel')}>{t('form_groups_title_labor_cost_title')}</Box>
          <Box className={cx('titleValue')}> {formatCurrencyUSD({ value: laborCost, showDecimal: true })}</Box>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default GroupsQuoteForm;
